/**
 * useAutoSyncAvatar — Sincroniza avatares de contatos sem foto em background.
 */
import { useEffect, useCallback } from 'react';
import { ChatConversation } from './types';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { invalidateEnrichmentCache } from '../../../services/repositories/helpers/ConversationEnrichmentHelper';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { isTemporaryRemoteMediaUrl } from '../../../utils/failedImageCache';
import { extractNumber } from '../../../utils/phoneUtils';

const MAX_BATCH_SIZE = 8;
const BATCH_INTERVAL_MS = 1200;
const attemptedKeys = new Set<string>();
let globalIsRunning = false;
let globalLastRun = 0;
const COOLDOWN_MS = 45_000;
let circuitBrokenUntil = 0;
const CIRCUIT_BREAK_MS = 5 * 60 * 1000;

type SyncResult = 'synced' | 'no_picture' | 'failed';

function conversationPhone(conversation: ChatConversation): string {
    return extractNumber(conversation.id).replace(/\D/g, '');
}

function syncKey(conversation: ChatConversation): string {
    return conversation.contactId || conversationPhone(conversation) || conversation.id;
}

function needsAvatarSync(conversation: ChatConversation): boolean {
    if (conversation.platform === 'instagram') return false;

    const key = syncKey(conversation);
    if (!key || attemptedKeys.has(key)) return false;

    const phone = conversationPhone(conversation);
    if (!conversation.contactId && phone.length < 8) return false;

    if (!conversation.avatar) return true;
    return isTemporaryRemoteMediaUrl(conversation.avatar);
}

export function useAutoSyncAvatar(
    conversations: ChatConversation[],
    enabled: boolean = true,
    instanceName?: string,
    connectionId?: string | null
) {
    const { user } = useAuth();
    const { currentOrganization } = useOrganization();

    const syncSingle = useCallback(async (conversation: ChatConversation): Promise<SyncResult> => {
        const key = syncKey(conversation);
        const phone = conversationPhone(conversation);
        const force = !conversation.avatar || isTemporaryRemoteMediaUrl(conversation.avatar);

        try {
            const { data, error } = await supabase.functions.invoke('sync-contact-avatar', {
                body: {
                    contactId: conversation.contactId,
                    phone,
                    organizationId: currentOrganization?.id,
                    instanceName,
                    connectionId,
                    force,
                }
            });

            if (error) return 'failed';
            if (data?.avatar) {
                attemptedKeys.add(key);
                return 'synced';
            }
            // already_synced = R2 ok. no_picture = WA confirmou ausência.
            // Qualquer outro null (ex.: persist falhou) → failed, tenta de novo no próximo ciclo.
            if (data?.message === 'already_synced') {
                attemptedKeys.add(key);
                return data?.avatar ? 'synced' : 'no_picture';
            }
            if (data?.message === 'no_picture') {
                attemptedKeys.add(key);
                return 'no_picture';
            }
            return 'failed';
        } catch {
            return 'failed';
        }
    }, [currentOrganization?.id, instanceName, connectionId]);

    useEffect(() => {
        if (!enabled || !user || !conversations.length || !currentOrganization?.id) return;

        const now = Date.now();
        if (now - globalLastRun < COOLDOWN_MS) return;
        if (globalIsRunning) return;
        if (now < circuitBrokenUntil) return;

        const needSync = [...new Map(
            conversations.filter(needsAvatarSync).map((conversation) => [syncKey(conversation), conversation]),
        ).values()];
        if (needSync.length === 0) return;

        let cancelled = false;
        globalIsRunning = true;
        globalLastRun = now;

        (async () => {
            try {
                const probeResult = await syncSingle(needSync[0]);
                let totalSynced = probeResult === 'synced' ? 1 : 0;
                let consecutiveFailures = probeResult === 'failed' ? 1 : 0;
                const remaining = needSync.slice(1);

                for (let i = 0; !cancelled && i < remaining.length; i += MAX_BATCH_SIZE) {
                    const batch = remaining.slice(i, i + MAX_BATCH_SIZE);
                    const results = await Promise.all(batch.map((conv) => syncSingle(conv)));

                    const synced = results.filter((r) => r === 'synced').length;
                    totalSynced += synced;

                    const failures = results.filter((r) => r === 'failed').length;
                    if (failures === batch.length) {
                        consecutiveFailures++;
                    } else {
                        consecutiveFailures = 0;
                    }

                    if (consecutiveFailures >= 3) {
                        circuitBrokenUntil = Date.now() + CIRCUIT_BREAK_MS;
                        remaining.slice(i + MAX_BATCH_SIZE).forEach((conv) => attemptedKeys.add(syncKey(conv)));
                        break;
                    }

                    if (i + MAX_BATCH_SIZE < remaining.length) {
                        await new Promise((r) => setTimeout(r, BATCH_INTERVAL_MS));
                    }
                }

                if (!cancelled && totalSynced > 0) {
                    invalidateEnrichmentCache(currentOrganization?.id);
                    window.dispatchEvent(new CustomEvent('avatar:synced'));
                }
            } catch {
                // Silencioso
            } finally {
                globalIsRunning = false;
            }
        })();

        return () => { cancelled = true; };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, conversations.length, currentOrganization?.id, instanceName, connectionId]);
}
